import * as Yup from "yup";
import { TBitValue, TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  ASSIGN_SUBSCRIPTION,
  GET_ASSIGNABLE_SUBSCRIPTION_PACKAGE,
  GET_FIRM_SUBSCRIPTION_DETAIL,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";

export interface IAssignableSubscriptionPackage {
  id: string;
  name: string;
  base_price: number;
  validity: number;
  sgst: number;
  cgst: number;
  igst: number;
  total: number;
}

interface IFirmLatestSubscription {
  subscription_package_id: string;
  subscription_package_name: string;
  start_date: Date;
  end_date: Date;
  remaining_days: number;
  is_expired: TBitValue;
}

export interface IFirmSubscriptionDetail {
  firm_id: string;
  firm_name: string;
  subscription: IFirmLatestSubscription | null;
}

export interface IFirmSubscriptionAssignmentForm {
  firm_id: string;
  subscription_package_id: string;
}

export const FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES: IFirmSubscriptionAssignmentForm =
  {
    firm_id: "",
    subscription_package_id: "",
  };

export const firmSubscriptionAssignmentValidationSchema = (
  t: TUseTranslationTfn
) =>
  Yup.object().shape({
    firm_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("firm_name")],
      ])
    ),
    subscription_package_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("subscription_package")],
      ])
    ),
  });

export const fetchAssignableSubscriptionPackage = async (
  setSubscriptionPackageList: TReactSetState<IAssignableSubscriptionPackage[]>
) => {
  try {
    const subscriptionPackage = await GET_ASSIGNABLE_SUBSCRIPTION_PACKAGE();
    if (subscriptionPackage?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setSubscriptionPackageList(subscriptionPackage.data ?? []);
    } else {
      setSubscriptionPackageList([]);
      toastError(subscriptionPackage?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e) {
    handleError(e, "fetchAssignableSubscriptionPackage");
  }
};

export const fetchFirmSubscriptionDetail = async (
  firmId: string,
  setFirmSubscriptionDetail: TReactSetState<IFirmSubscriptionDetail | undefined>,
  setLoading: TReactSetState<boolean>
) => {
  setLoading(true);
  try {
    const firmSubscriptionDetail = await GET_FIRM_SUBSCRIPTION_DETAIL(firmId);
    if (firmSubscriptionDetail?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setFirmSubscriptionDetail(firmSubscriptionDetail.data);
    } else {
      setFirmSubscriptionDetail(undefined);
      toastError(
        firmSubscriptionDetail?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED
      );
    }
  } catch (e) {
    setFirmSubscriptionDetail(undefined);
    handleError(e, "fetchFirmSubscriptionDetail");
  } finally {
    setLoading(false);
  }
};

export const handleSubmitFirmSubscriptionAssignment = async (
  values: IFirmSubscriptionAssignmentForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void
) => {
  setLoading(true);
  try {
    const assignedSubscription = await ASSIGN_SUBSCRIPTION(values);
    if (assignedSubscription?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: assignedSubscription.message });
      return handleClose(true);
    }
    sweetAlertError({
      message: assignedSubscription?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
    });
  } catch (e) {
    handleError(e, "handleSubmitFirmSubscriptionAssignment");
  } finally {
    setLoading(false);
  }
};
